// src/components/tutor/mobile/MobileTutorAnnouncements.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiUser,
  FiClock,
  FiChevronRight,
  FiArrowLeft,
  FiSend,
  FiPaperclip,
} from "react-icons/fi";
import { BsFillPinAngleFill } from "react-icons/bs";
import { auth, db } from "../../../firebaseConfig";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import "./MobileAnnouncements.css";

// ============================================================================
// LOGIC
// ============================================================================

function subscribeTutorAnnouncements(uid, onRows) {
  if (!uid) return () => {};

  const qAll = query(
    collection(db, "announcements"),
    where("audienceType", "==", "all")
  );
  const qUsers = query(
    collection(db, "announcements"),
    where("audienceType", "==", "users"),
    where("audienceUserIds", "array-contains", uid)
  );

  const sourceAll = new Map();
  const sourceUsers = new Map();

  const withMs = (id, data) => ({
    id,
    ...data,
    createdAtMs: data?.createdAt?.toMillis?.() ?? null,
  });

  const recompute = () => {
    const merged = new Map();
    for (const [id, v] of sourceAll) merged.set(id, v);
    for (const [id, v] of sourceUsers) merged.set(id, v);
    onRows(Array.from(merged.values()));
  };

  const unsubAll = onSnapshot(qAll, (snap) => {
    sourceAll.clear();
    snap.docs.forEach((d) => sourceAll.set(d.id, withMs(d.id, d.data())));
    recompute();
  });

  const unsubUsers = onSnapshot(qUsers, (snap) => {
    sourceUsers.clear();
    snap.docs.forEach((d) => sourceUsers.set(d.id, withMs(d.id, d.data())));
    recompute();
  });

  return () => { unsubAll(); unsubUsers(); };
}

function subscribeCategories(onRows) {
  const q = query(collection(db, "announcement_categories"), orderBy("name", "asc"));
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => onRows([])
  );
}

function subscribeMyReadStatus(announcementId, onStatus) {
  const uid = auth.currentUser?.uid;
  if (!uid) return () => {};
  const ref = doc(db, "announcements", announcementId, "reads", uid);
  return onSnapshot(ref, (snap) => onStatus(snap.exists() ? snap.data() : null));
}

async function markAsRead(announcementId) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const user = auth.currentUser;
  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.exists() ? snap.data() : {};
  const ref = doc(db, "announcements", announcementId, "reads", uid);
  await setDoc(
    ref,
    {
      uid,
      userName: data.name || user.displayName || "User",
      userEmail: data.email || user.email || "",
      userRole: data.role || "tutor",
      readAt: serverTimestamp(),
      method: "tap",
    },
    { merge: true }
  );
}

function subscribeAnnouncementAttachments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "attachments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => onRows([])
  );
}

function subscribeComments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "comments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => onRows([])
  );
}

async function addComment(announcementId, text) {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const user = auth.currentUser;
  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.exists() ? snap.data() : {};
  const clean = (text || "").replace(/​/g, "").replace(/ /g, " ").trim();
  if (!clean) return;
  await addDoc(collection(db, "announcements", announcementId, "comments"), {
    uid,
    userName: data.name || user.displayName || "User",
    userEmail: data.email || user.email || "",
    userRole: data.role || "tutor",
    text: clean,
    parentId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// HELPERS
// ============================================================================

function isToday(ts) {
  if (!ts?.toDate) return false;
  return ts.toDate().toDateString() === new Date().toDateString();
}

function formatListDate(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return (
    d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
    " at " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

function formatDetailDate(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  return (
    d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) +
    " at " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

// ============================================================================
// COMPONENTS
// ============================================================================

// --- Filter Pills ---
function FilterPills({ pills, active, onSelect }) {
  return (
    <div className="man-pills-scroll">
      <div className="man-pills-row">
        {pills.map((p) => (
          <button
            key={p}
            className={`man-pill ${active === p.toLowerCase() ? "man-pill-active" : ""}`}
            onClick={() => onSelect(p.toLowerCase())}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Announcement Row ---
function AnnouncementRow({ announcement, isRead, onSelect }) {
  const author = announcement.createdByName || announcement.authorName || "Admin";
  const dateStr = formatListDate(announcement.createdAt);

  return (
    <div className="man-row" onClick={() => onSelect(announcement)}>
      <div className="man-dot-col">
        {!isRead && <span className="man-unread-dot" />}
      </div>
      <div className="man-row-body">
        <div className="man-row-title">{announcement.title}</div>
        <div className="man-row-meta">
          <FiUser size={11} />
          <span>{author}</span>
          <FiClock size={11} />
          <span>{dateStr}</span>
        </div>
      </div>
      <FiChevronRight className="man-row-chevron" size={16} />
    </div>
  );
}

// --- Group Section ---
function GroupSection({ label, items, readMap, onSelect }) {
  if (!items.length) return null;
  return (
    <div className="man-group">
      <div className="man-group-label">{label}</div>
      {items.map((a) => (
        <AnnouncementRow
          key={a.id}
          announcement={a}
          isRead={!!readMap[a.id]}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// --- Comments Section ---
function CommentsSection({ announcementId, commentsEnabled }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!announcementId) return;
    return subscribeComments(announcementId, setComments);
  }, [announcementId]);

  const handlePost = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      await addComment(announcementId, text);
      setText("");
    } catch (err) {
      console.error("Failed to post comment:", err);
    } finally {
      setPosting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  return (
    <div className="man-comments">
      <div className="man-comments-title">Comments</div>

      {comments.length === 0 && (
        <div className="man-comments-empty">No comments yet.</div>
      )}

      <div className="man-comments-list">
        {comments.map((c) => (
          <div key={c.id} className="man-comment-item">
            <div className="man-comment-top">
              <span className="man-comment-author">{c.userName || "User"}</span>
              <span className="man-comment-time">
                {c.createdAt?.toDate
                  ? c.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : ""}
              </span>
            </div>
            <div className="man-comment-text">{c.text}</div>
          </div>
        ))}
      </div>

      {commentsEnabled ? (
        <div className="man-comment-input-row">
          <input
            className="man-comment-input"
            placeholder="Add a comment..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="man-comment-send"
            onClick={handlePost}
            disabled={!text.trim() || posting}
          >
            <FiSend size={14} />
          </button>
        </div>
      ) : (
        <div className="man-comments-disabled">Comments are disabled.</div>
      )}
    </div>
  );
}

// --- Detail View ---
function AnnouncementDetailView({ announcement, onBack }) {
  const [attachments, setAttachments] = useState([]);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const commentsEnabled = !(
    announcement?.allowComments === false ||
    announcement?.allowComments === "false" ||
    announcement?.commentsEnabled === false ||
    announcement?.commentsEnabled === "false"
  );

  useEffect(() => {
    if (!announcement?.id) return;
    return subscribeAnnouncementAttachments(announcement.id, setAttachments);
  }, [announcement?.id]);

  const author = announcement.createdByName || announcement.authorName || "Admin";
  const dateStr = formatDetailDate(announcement.createdAt);

  // Swipe-to-back: left → right swipe anywhere on the detail screen triggers onBack.
  // Requires the swipe to be more horizontal than vertical (avoids firing during scrolling).
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    // Swipe right ≥80 px and clearly more horizontal than vertical
    if (dx > 80 && dx > dy * 1.5) {
      onBack();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div
      className="man-detail"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sticky Header */}
      <div className="man-detail-header">
        <button className="man-back-btn" onClick={onBack}>
          <FiArrowLeft size={22} />
          <span>Announcements</span>
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="man-detail-scroll">
        {/* Pinned Badge */}
        {announcement.pinned && (
          <div className="man-detail-pinned">
            <BsFillPinAngleFill size={11} />
            <span>Pinned</span>
          </div>
        )}

        {/* Title */}
        <h1 className="man-detail-title">{announcement.title}</h1>

        {/* Meta */}
        <div className="man-detail-meta">
          <FiUser size={13} />
          <span>{author}</span>
          <span className="man-meta-sep">·</span>
          <FiClock size={13} />
          <span>{dateStr}</span>
        </div>

        {/* Divider */}
        <div className="man-detail-divider" />

        {/* Body */}
        <div
          className="man-detail-body"
          dangerouslySetInnerHTML={{ __html: announcement.contentHtml || "" }}
        />

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="man-detail-attachments">
            <div className="man-attachments-label">Attachments</div>
            {attachments.map((a) => (
              <a
                key={a.id}
                href={a.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="man-attach-link"
              >
                <FiPaperclip size={13} />
                <span>{a.fileName}</span>
              </a>
            ))}
          </div>
        )}

        {/* Comments */}
        <CommentsSection
          announcementId={announcement.id}
          commentsEnabled={commentsEnabled}
        />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN EXPORT
// ============================================================================
export default function MobileTutorAnnouncements({ pendingAnnouncementId, onAnnouncementOpened, onUnreadCountChange }) {
  const uid = auth.currentUser?.uid;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [readMap, setReadMap] = useState({});
  const [selected, setSelected] = useState(null);
  const readUnsubsRef = useRef({});
  // Track which pending ID we've already handled so we don't re-open on re-renders
  const handledPendingRef = useRef(null);

  // Subscribe to announcements
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    const unsub = subscribeTutorAnnouncements(uid, (data) => {
      setRows(data);
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  // Subscribe to categories
  useEffect(() => {
    return subscribeCategories(setCategories);
  }, []);

  // Cleanup all read subscriptions on unmount
  useEffect(() => {
    const unsubsMap = readUnsubsRef.current;
    return () => {
      Object.values(unsubsMap).forEach((unsub) => unsub());
    };
  }, []);

  // Deep-link: open a specific announcement when pendingAnnouncementId arrives.
  // Uses getDoc() directly — no dependency on the feed list being loaded,
  // no race condition on slow connections.
  useEffect(() => {
    if (!pendingAnnouncementId) return;
    if (pendingAnnouncementId === handledPendingRef.current) return;
    handledPendingRef.current = pendingAnnouncementId;

    getDoc(doc(db, "announcements", pendingAnnouncementId))
      .then((snap) => {
        if (!snap.exists()) return;
        const ann = { id: snap.id, ...snap.data() };
        setSelected(ann);
        markAsRead(ann.id).catch(console.error);
        onAnnouncementOpened?.();
      })
      .catch(console.error);
  }, [pendingAnnouncementId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter: archived out, then active date window
  const visible = useMemo(() => {
    const now = Date.now();
    return rows.filter((a) => {
      if (a.archived) return false;
      const start = a.startAt?.toMillis?.() ?? 0;
      const end = a.endAt?.toMillis?.() ?? null;
      if (start && now < start) return false;
      if (end && now > end) return false;
      return true;
    });
  }, [rows]);

  // Report unread count to parent (for nav badge) whenever visible or readMap changes
  useEffect(() => {
    if (!onUnreadCountChange) return;
    const count = visible.filter((a) => !readMap[a.id]).length;
    onUnreadCountChange(count);
  }, [visible, readMap, onUnreadCountChange]);

  // Sort: Pinned > orderIndex > date
  const sorted = useMemo(() => {
    return [...visible].sort((a, b) => {
      if (a.pinned !== b.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      const ao = typeof a.orderIndex === "number" ? a.orderIndex : a.createdAtMs ?? 0;
      const bo = typeof b.orderIndex === "number" ? b.orderIndex : b.createdAtMs ?? 0;
      if (ao !== bo) return bo - ao;
      return (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0);
    });
  }, [visible]);

  // Subscribe to read status for each visible announcement
  const sortedIdsKey = useMemo(() => sorted.map((a) => a.id).join(","), [sorted]);

  useEffect(() => {
    if (!uid) return;
    const ids = sorted.map((a) => a.id);

    // Unsubscribe from IDs no longer visible
    Object.keys(readUnsubsRef.current).forEach((id) => {
      if (!ids.includes(id)) {
        readUnsubsRef.current[id]();
        delete readUnsubsRef.current[id];
      }
    });

    // Subscribe to newly visible IDs
    ids.forEach((id) => {
      if (!readUnsubsRef.current[id]) {
        readUnsubsRef.current[id] = subscribeMyReadStatus(id, (status) => {
          setReadMap((prev) => ({ ...prev, [id]: status }));
        });
      }
    });
  }, [sortedIdsKey, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply filter
  const filtered = useMemo(() => {
    if (activeFilter === "all") return sorted;
    if (activeFilter === "pinned") return sorted.filter((a) => a.pinned);
    return sorted.filter(
      (a) => (a.category || "").toLowerCase() === activeFilter.toLowerCase()
    );
  }, [sorted, activeFilter]);

  // Group by Today / Earlier
  const { todayItems, earlierItems } = useMemo(() => {
    const todayItems = [];
    const earlierItems = [];
    filtered.forEach((a) => {
      if (isToday(a.createdAt)) todayItems.push(a);
      else earlierItems.push(a);
    });
    return { todayItems, earlierItems };
  }, [filtered]);

  // Build pill labels: All + Pinned + categories (collection + announcement data, mirroring desktop)
  const pills = useMemo(() => {
    const set = new Set(["General", "Updates"]);
    categories.forEach((c) => { if (c.name?.trim()) set.add(c.name.trim()); });
    sorted.forEach((a) => { if (a.category?.trim()) set.add(a.category.trim()); });
    const extra = [...set].sort((a, b) => a.localeCompare(b));
    return ["All", "Pinned", ...extra];
  }, [categories, sorted]);

  // Tap a row: mark as read + open detail
  const handleSelect = async (announcement) => {
    setSelected(announcement);
    if (!readMap[announcement.id]) {
      try {
        await markAsRead(announcement.id);
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
  };

  // Render detail view
  if (selected) {
    return (
      <AnnouncementDetailView
        announcement={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  // Render feed
  return (
    <div className="man-container">
      <div className="man-header">
        <h1 className="man-title">Announcements</h1>
      </div>

      <FilterPills pills={pills} active={activeFilter} onSelect={setActiveFilter} />

      <div className="man-feed">
        {loading && <div className="man-state-msg">Loading...</div>}

        {!loading && filtered.length === 0 && (
          <div className="man-state-msg">No announcements.</div>
        )}

        <GroupSection
          label="Today"
          items={todayItems}
          readMap={readMap}
          onSelect={handleSelect}
        />

        <GroupSection
          label="Earlier"
          items={earlierItems}
          readMap={readMap}
          onSelect={handleSelect}
        />
      </div>
    </div>
  );
}
